import WhatsappIcon from '@mui/icons-material/WhatsApp'
import { Box, styled } from '@mui/material'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import React, { useState } from 'react'

import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { getWALanguageMap } from '@/app/module/campaigns/component/item/steps/content/types/multimessage/content'
import CampaignContentWhatsappMultilingual from '@/app/module/campaigns/component/item/steps/content/whatsapp-multilingual'
import { WhatsAppMessageType } from '@/app/module/campaigns/component/item/steps/content/whatsapp-props'
import { getInitialWaTab, toTranslation } from '@/app/module/campaigns/component/item/steps/content/whatsapp-utils'
import { LanguageType } from '@/app/module/campaigns/helpers'
import {
  ContentWhatsApp,
  ImageProps,
  LocalActionType,
  LocalSendWhatsAppAction,
  PartFamily,
  VoiceProps,
} from '@/app/module/campaigns/types'
import { WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { PersonalizationType } from '@/app/types'

type Props = {
  defaultLanguage: string
  imageProps: Pick<ImageProps, 'getFile' | 'loading' | 'saveFileHandler'>
  languages: LanguageType[]
  personalizationList: PersonalizationType
  value: LocalSendWhatsAppAction
  voiceProps: Pick<
    VoiceProps,
    'files' | 'loading' | 'playId' | 'getFile' | 'onMicAccessError' | 'saveFileHandler' | 'setPlaying'
  >
  onClose: () => void
  onSave: (value: Partial<LocalSendWhatsAppAction>) => void
}

const CampaignContentQuestionDialogWhatsapp: React.FC<Props> = ({
  imageProps,
  voiceProps,
  value,
  onClose,
  onSave,
  ...props
}) => {
  // Create a local copy of the entire whatsapp content structure
  const [localContent, setLocalWhatsAppContent] = useState<ContentWhatsApp>(() => {
    return 'sendWhatsapp' in value
      ? value.sendWhatsapp
      : {
          text: '',
          image: '',
          audio: '',
          video: '',
          document: '',
          translations: {},
          registerTemplate: false,
        }
  })
  const [activeTab, setActiveTab] = React.useState<WhatsAppTab>(getInitialWaTab(localContent))

  const item = React.useMemo<WhatsAppMessageType>(
    () => ({
      changed: false,
      hasQuestion: false,
      id: 'whatsapp-message',
      message: localContent,
      type: PartFamily.WhatsApp,
    }),
    [localContent],
  )

  const handleSave = React.useCallback(() => {
    onSave(toAction(value, localContent))
  }, [localContent, value, onSave])

  const handleChange = React.useCallback((updatedItem: WhatsAppMessageType) => {
    setLocalWhatsAppContent(updatedItem.message)
  }, [])

  const languageContentMap = React.useMemo(
    () => getWALanguageMap(item.message, props.defaultLanguage),
    [item, props.defaultLanguage],
  )
  const canSave = Object.values(languageContentMap).some(Boolean)

  return (
    <CustomDialog maxWidth="md" fullWidth={true} open={true} onClose={onClose}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <IconText>
            <WhatsappIcon />
            <Typography variant="h6">Send WhatsApp message to contact</Typography>
          </IconText>

          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <StyledDialogContent>
        <Content>
          <CampaignContentWhatsappMultilingual
            {...props}
            {...voiceProps}
            {...imageProps}
            changeDelay={0}
            filterType="whatsapp"
            info=""
            item={item}
            label="WhatsApp message"
            personalizationList={props.personalizationList}
            tab={activeTab}
            changeHandler={handleChange}
            onTabChange={setActiveTab}
          />
        </Content>
      </StyledDialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={!canSave}
          loading={imageProps.loading || voiceProps.loading}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </CustomDialog>
  )
}

const StyledDialogContent = styled(DialogContent)({
  paddingTop: 8,
})

const Content = styled(Box)({
  width: '70vw',
  minWidth: '300px',
  maxWidth: '100%',
})

const toAction = (
  originalAction: LocalSendWhatsAppAction,
  content: ContentWhatsApp,
): Omit<LocalSendWhatsAppAction, 'id'> => {
  if (!('sendWhatsapp' in originalAction)) {
    return {
      type: LocalActionType.SendWhatsApp,
      sendWhatsapp: {
        ...toTranslation(content),
        registerTemplate: content.registerTemplate,
        senderId: '',
        translations: content.translations,
        useCampaignSenderId: 'senderIdWhatsapp',
      },
    }
  }

  return {
    ...originalAction,
    type: LocalActionType.SendWhatsApp,
    sendWhatsapp: {
      ...originalAction.sendWhatsapp,
      ...toTranslation(content),
      registerTemplate: content.registerTemplate,
      senderId: '',
      translations: content.translations,
      useCampaignSenderId: 'senderIdWhatsapp',
    },
  }
}

export default CampaignContentQuestionDialogWhatsapp

export type { Props as CampaignContentQuestionDialogWhatsappProps }
